import { useState } from 'preact/hooks'
import { RefreshCcw, Plus, AlertCircle, Star, ChevronRight } from 'lucide-preact'
import { DataGrid, type ColumnConfig } from '@/components/common/grids/DataGrid'
import { useSendingDomains } from '@/hooks/useSendingDomains'
import { invalidateVerifiedDomainsLookup } from '@/hooks/useVerifiedDomainsLookup'
import type { SendingDomainViewDto } from '@/types/sendingDomains'
import {
  COMPANY_WEBSITE_PLACE,
  domainListLines,
  newDomainWarmupSentence,
  regionLabel,
  type DomainStatusLine,
  type ListLineStyle,
} from '@/utils/sendingDomainStatus'
import { AddSendingDomainPanel } from './AddSendingDomainPanel'
import { DomainStatePill } from './DomainStatePill'
import { useLoadedCompany } from './useLoadedCompany'
import { useWarmupSummary } from './useWarmupSummary'
import { PRIMARY_BUTTON, SOFT_BUTTON } from './styles'

const LIST_STYLE: ListLineStyle = { place: COMPANY_WEBSITE_PLACE.settings, runOn: true }
const COMPANY_NOT_LOADED = "The company's details couldn't be loaded. Refresh to try again."

interface SendingDomainsListProps {
  clientCompanyId: string
}

export function SendingDomainsList({ clientCompanyId }: SendingDomainsListProps) {
  const { company, error: companyError, reload: reloadCompany } = useLoadedCompany(clientCompanyId)
  const companyDomain = company?.CanonicalDomain || null
  // Until the company arrives, every domain would read as having no company Website to check against.
  const waitingForCompany = !company && !companyError
  const { domains, loading, error, load, upsert, remove } = useSendingDomains(clientCompanyId)
  const { summary, error: summaryError, reload: reloadSummary } = useWarmupSummary(clientCompanyId)
  const [panelOpen, setPanelOpen] = useState(false)
  const [selected, setSelected] = useState<SendingDomainViewDto | null>(null)

  const refresh = () => {
    void load()
    void reloadSummary()
    void reloadCompany()
  }
  const openCreate = () => {
    setSelected(null)
    setPanelOpen(true)
  }
  const openDomain = (domain: SendingDomainViewDto) => {
    setSelected(domain)
    setPanelOpen(true)
  }
  const closePanel = () => {
    setPanelOpen(false)
    setSelected(null)
    refresh()
  }

  const columns: ColumnConfig<SendingDomainViewDto>[] = [
    {
      key: 'domain',
      header: 'Domain',
      width: '280px',
      className: 'align-top',
      render: domain => (
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="break-all font-medium text-slate-900 dark:text-white">{domain.Domain}</span>
            {domain.IsDefault && (
              // Grey, not green: green is Ready now.
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                <Star className="h-3 w-3 fill-current" aria-hidden="true" /> Default
              </span>
            )}
          </div>
          {domain.Region && <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">{regionLabel(domain.Region)}</p>}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      className: 'align-top',
      render: domain => (
        <div>
          <DomainStatePill domain={domain} />
          <StatusLines lines={domainListLines(domain, companyDomain, summary, LIST_STYLE)} />
        </div>
      ),
    },
    {
      key: 'open',
      header: '',
      width: '56px',
      align: 'right',
      className: 'align-top',
      render: () => <ChevronRight className="ml-auto h-4 w-4 text-slate-400" aria-hidden="true" />,
    },
  ]

  return (
    <div className="px-6 py-4 max-w-7xl mx-auto space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Sending Domains</h2>
          <p className="text-sm mt-1 text-slate-600 dark:text-slate-400 max-w-3xl">
            Authenticate the domains you send marketing email from, then select them on your email activations.
            {summary && ` ${newDomainWarmupSentence(summary.Steps)}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={refresh} disabled={loading} className={SOFT_BUTTON}>
            <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" /> {loading ? 'Refreshing' : 'Refresh'}
          </button>
          <button type="button" onClick={openCreate} disabled={!company} className={PRIMARY_BUTTON}>
            <Plus className="w-4 h-4" aria-hidden="true" /> Add domain
          </button>
        </div>
      </div>

      {(error || companyError) && (
        <div className="flex items-start gap-2 p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          <AlertCircle className="w-4 h-4 mt-0.5" aria-hidden="true" />
          <span>{error || COMPANY_NOT_LOADED}</span>
        </div>
      )}

      {summaryError && domains && domains.length > 0 && (
        <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Today's warm-up figures couldn't be loaded, so the list leaves them out. Refresh to try again.</span>
        </p>
      )}

      {!companyError && (domains || loading || waitingForCompany) && (
        <DataGrid
          title="Domains"
          data={domains ?? []}
          columns={columns}
          getRowKey={domain => domain.Id}
          isLoading={(loading && !domains) || waitingForCompany}
          onRowClick={openDomain}
          emptyMessage="No sending domains yet. Add one to start authenticating."
        />
      )}

      <AddSendingDomainPanel
        // A fresh panel per domain, so opening one never draws the last one's page (or fetches its figures) first.
        key={selected?.Id ?? 'new'}
        isOpen={panelOpen}
        clientCompanyId={clientCompanyId}
        domain={selected}
        domains={domains ?? []}
        summary={summary}
        onClose={closePanel}
        onSaved={saved => { upsert(saved); invalidateVerifiedDomainsLookup(); void reloadSummary() }}
        onDeleted={id => { remove(id); invalidateVerifiedDomainsLookup() }}
        onOpenDomain={openDomain}
      />
    </div>
  )
}

/** The sentences under a state pill, each lead-in in bold. */
function StatusLines({ lines }: { lines: DomainStatusLine[] }) {
  return (
    <>
      {lines.map((line, index) => (
        <p key={index} className={`${index === 0 ? 'mt-1.5' : 'mt-0.5'} max-w-2xl text-sm text-slate-600 dark:text-slate-400`}>
          {line.lead && <span className="font-medium text-slate-900 dark:text-white">{line.lead}</span>}
          {line.lead && line.text ? ' ' : ''}
          {line.text}
        </p>
      ))}
    </>
  )
}
